/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes, useMemo } from 'preact/compat';
import { classes, getGlobalLocale } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/Price/Price.css';

export interface PriceProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'size'> {
  amount?: number;
  currency?: string | null;
  locale?: string;
  formatOptions?: {
    [key: string]: any;
  };
  variant?: 'default' | 'strikethrough';
  weight?: 'bold' | 'normal';
  sale?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const Price: FunctionComponent<PriceProps> = ({
  amount = 0,
  currency,
  locale,
  variant = 'default',
  weight = 'bold',
  className,
  children,
  sale = false,
  formatOptions = {},
  size = 'small',
  ...props
}) => {
  // Determine the locale to use: prop locale > global locale > browser locale
  const effectiveLocale = useMemo(() => {
    if (locale) {
      return locale;
    }
    const globalLocale = getGlobalLocale();
    if (globalLocale) {
      return globalLocale;
    }
    // Fallback to browser locale or default
    return process.env.LOCALE && process.env.LOCALE !== 'undefined' ? process.env.LOCALE : 'en-US';
  }, [locale]);

  const formatter = useMemo(
    () => {
      const params: Intl.NumberFormatOptions = {
        style: 'currency',
        currency: currency || 'USD',
        // These options are needed to round to whole numbers if that's what you want.
        minimumFractionDigits: 2, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
        maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
        ...formatOptions,
      }
      try {
        return new Intl.NumberFormat(effectiveLocale, params);
      } catch (error) {
        console.error(`Error creating Intl.NumberFormat instance for locale ${effectiveLocale}. Falling back to en-US.`, error);
        return new Intl.NumberFormat('en-US', params);
      }
    },
    [effectiveLocale, currency, formatOptions]
  );

  const formattedAmount = useMemo(
    () => formatter.format(amount),
    [amount, formatter]
  );

  return (
    <span
      {...props}
      className={classes([
        'dropin-price',
        `dropin-price--${variant}`,
        `dropin-price--${size}`,
        `dropin-price--${weight}`,
        ['dropin-price--sale', sale],
        className,
      ])}
    >
      {formattedAmount}
    </span>
  );
};
